import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';

/**
 * Enhanced Onboarding Context with comprehensive analytics tracking
 */
const OnboardingContext = createContext(null);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  // Always call useAnalytics hook unconditionally
  const { trackEvent, startTiming, endTiming } = useAnalytics();
  
  // Onboarding state
  const [currentStep, setCurrentStep] = useState(1);
  const [isCompleted, setIsCompleted] = useState(false);
  const [onboardingData, setOnboardingData] = useState({
    // Step 1 - Personal Information
    userName: '',
    jobTitle: '',
    hearAboutUs: '',
    hearAboutUsOther: '',
    
    // Step 2 - Company Information
    companyName: '',
    industry: '',
    industryOther: '',
    companySize: '',
    
    // Step 3 - AI Requirements
    primaryUseCase: '',
    primaryUseCaseOther: '',
    mainGoals: [],
    expectedVolume: '',
    
    // Metadata
    startedAt: null,
    completedAt: null,
    totalTimeSpent: 0,
    stepTimes: {},
    validationErrors: {},
    abandonedSteps: []
  });
  
  // Analytics tracking refs
  const stepStartTime = useRef({});
  const sessionStartTime = useRef(null);
  const stepValidationAttempts = useRef({});
  const fieldInteractions = useRef({});
  

  
  /**
   * Initialize onboarding session
   */
  const initializeOnboarding = useCallback(() => {
    const now = Date.now();
    sessionStartTime.current = now;
    
    setOnboardingData(prev => ({
      ...prev,
      startedAt: new Date().toISOString()
    }));
    
    // Track onboarding started
    trackEvent('onboarding_started', {
      timestamp: new Date().toISOString(),
      entry_point: window.location.pathname
    });
    
    // Start timing
    startTiming('onboarding', 'complete_flow');
  }, [trackEvent, startTiming]);
  
  /**
   * Track step start
   */
  const trackStepStart = useCallback((stepNumber, stepName) => {
    const now = Date.now();
    stepStartTime.current[stepNumber] = now;
    
    // Track step started event
    trackEvent('onboarding_step_started', {
      step_number: stepNumber,
      step_name: stepName,
      time_since_start: sessionStartTime.current ? 
        Math.round((now - sessionStartTime.current) / 1000) : 0
    });
    
    // Start timing for this step
    startTiming('onboarding_step', stepName);
  }, [trackEvent, startTiming]);
  
  /**
   * Track step completion
   */
  const trackStepCompleted = useCallback((stepNumber, stepName) => {
    const now = Date.now();
    const timeSpent = stepStartTime.current[stepNumber] ? 
      Math.round((now - stepStartTime.current[stepNumber]) / 1000) : 0;
    
    // Update step times
    setOnboardingData(prev => ({
      ...prev,
      stepTimes: {
        ...prev.stepTimes,
        [stepName]: timeSpent
      }
    }));
    
    // Track completion event
    trackEvent('onboarding_step_completed', {
      step_number: stepNumber,
      step_name: stepName,
      time_spent: timeSpent,
      fields_completed: getStepFields(stepNumber),
      validation_attempts: stepValidationAttempts.current[stepNumber] || 0,
      completion_method: 'button_click'
    });
    
    // End timing for this step
    endTiming('onboarding_step', stepName);
  }, [trackEvent, endTiming]);
  
  /**
   * Track step skip/abandonment
   */
  const trackStepSkipped = useCallback((stepNumber, stepName, reason = 'skipped') => {
    const now = Date.now();
    const timeSpent = stepStartTime.current[stepNumber] ? 
      Math.round((now - stepStartTime.current[stepNumber]) / 1000) : 0;
    
    // Track abandoned steps
    setOnboardingData(prev => ({
      ...prev,
      abandonedSteps: [...prev.abandonedSteps, { stepNumber, stepName, reason }]
    }));
    
    // Track event
    trackEvent('onboarding_step_abandoned', {
      step_number: stepNumber,
      step_name: stepName,
      time_spent: timeSpent,
      reason: reason,
      last_field: Object.keys(fieldInteractions.current[stepNumber] || {}).pop()
    });
  }, [trackEvent]);
  
  /**
   * Track validation failure
   */
  const trackValidationFailure = useCallback((stepNumber, fieldName, errorType) => {
    // Increment validation attempts
    if (!stepValidationAttempts.current[stepNumber]) {
      stepValidationAttempts.current[stepNumber] = 0;
    }
    stepValidationAttempts.current[stepNumber]++;
    
    // Track event
    trackEvent('onboarding_validation_failed', {
      step_number: stepNumber,
      field_name: fieldName,
      error_type: errorType,
      attempt_number: stepValidationAttempts.current[stepNumber]
    });
  }, [trackEvent]);
  
  /**
   * Track field interaction
   */
  const trackFieldInteraction = useCallback((stepNumber, fieldName, action, value = null) => {
    // Initialize field tracking
    if (!fieldInteractions.current[stepNumber]) {
      fieldInteractions.current[stepNumber] = {};
    }
    if (!fieldInteractions.current[stepNumber][fieldName]) {
      fieldInteractions.current[stepNumber][fieldName] = {
        focuses: 0,
        changes: 0,
        blurs: 0
      };
    }
    
    // Update interaction count
    if (action === 'focus') {
      fieldInteractions.current[stepNumber][fieldName].focuses++;
    } else if (action === 'change') {
      fieldInteractions.current[stepNumber][fieldName].changes++;
    } else if (action === 'blur') {
      fieldInteractions.current[stepNumber][fieldName].blurs++;
    }
    
    // Track specific field events based on field type
    const eventName = getFieldEventName(fieldName, action);
    if (eventName) {
      const properties = {
        step_number: stepNumber,
        field_name: fieldName,
        action: action
      };
      
      // Add special handling for "Other" options
      if (fieldName.includes('Other') && value) {
        properties.is_other = true;
        properties.custom_value = value;
        properties.custom_value_length = value.length;
      } else if (value) {
        properties.has_value = true;
        properties.value_length = value.toString().length;
      }
      
      trackEvent(eventName, properties);
    }
  }, [trackEvent]);

  /**
   * Track onboarding completion
   */
  const trackOnboardingCompleted = useCallback((completionPath) => {
    const now = Date.now();
    const totalTime = sessionStartTime.current ? 
      Math.round((now - sessionStartTime.current) / 1000) : 0;
    
    // Update completion data
    setOnboardingData(prev => ({
      ...prev,
      completedAt: new Date().toISOString(),
      totalTimeSpent: totalTime
    }));
    
    // Track completion event
    trackEvent('onboarding_completed', {
      total_time: totalTime,
      completion_path: completionPath,
      step_times: onboardingData.stepTimes,
      abandoned_steps: onboardingData.abandonedSteps.length,
      total_validation_attempts: Object.values(stepValidationAttempts.current)
        .reduce((sum, val) => sum + val, 0)
    });
    
    // Track decision point
    trackEvent('onboarding_exit_choice', {
      choice: completionPath,
      time_to_decision: totalTime
    });
    
    // End timing
    endTiming('onboarding', 'complete_flow');
    
    setIsCompleted(true);
  }, [trackEvent, endTiming, onboardingData.stepTimes, onboardingData.abandonedSteps]);
  
  // Helper functions - moved up to avoid initialization issues
  const getCollectedDataSummary = useCallback(() => {
    const fields = [
      'userName', 'jobTitle', 'hearAboutUs', 'companyName', 
      'industry', 'companySize', 'primaryUseCase', 'mainGoals', 
      'expectedVolume'
    ];
    
    return fields.reduce((summary, field) => {
      summary[field] = !!onboardingData[field];
      return summary;
    }, {});
  }, [onboardingData]);

  /**
   * Track onboarding abandonment
   */
  const trackOnboardingAbandoned = useCallback((lastStep, reason) => {
    const now = Date.now();
    const totalTime = sessionStartTime.current ? 
      Math.round((now - sessionStartTime.current) / 1000) : 0;
    
    trackEvent('onboarding_abandoned', {
      last_step: lastStep,
      reason: reason,
      total_time: totalTime,
      completed_steps: Object.keys(onboardingData.stepTimes).length,
      data_collected: getCollectedDataSummary()
    });
  }, [trackEvent, onboardingData.stepTimes, getCollectedDataSummary]);
  
  /**
   * Track special field selections (for "Other" options)
   */
  const trackSpecialFieldSelection = useCallback((fieldName, value, isOther) => {
    const baseEventName = getBaseEventName(fieldName);
    
    if (baseEventName) {
      const properties = {
        [fieldName]: value,
        is_other: isOther
      };
      
      // Add custom value if "Other" was selected
      if (isOther && onboardingData[`${fieldName}Other`]) {
        properties.custom_value = onboardingData[`${fieldName}Other`];
      }
      
      // Add additional context based on field
      if (fieldName === 'hearAboutUs') {
        properties.source = value;
        properties.custom_source = isOther ? onboardingData.hearAboutUsOther : null;
      } else if (fieldName === 'industry') {
        properties.industry = value;
        properties.custom_industry = isOther ? onboardingData.industryOther : null;
        // Simple industry categorization
        const industryCategories = {
          'technology': 'tech',
          'healthcare': 'health',
          'finance': 'finance',
          'retail': 'commerce',
          'education': 'education'
        };
        properties.industry_category = industryCategories[value] || 'other';
      } else if (fieldName === 'primaryUseCase') {
        properties.use_case = value;
        properties.custom_use_case = isOther ? onboardingData.primaryUseCaseOther : null;
        properties.complexity_level = getUseCaseComplexity(value);
      }
      
      trackEvent(baseEventName, properties);
    }
  }, [trackEvent, onboardingData]);
  
  // Helper functions
  const getStepFields = (stepNumber) => {
    const stepFieldMap = {
      1: ['userName', 'jobTitle', 'hearAboutUs'],
      2: ['companyName', 'industry', 'companySize'],
      3: ['primaryUseCase', 'mainGoals', 'expectedVolume']
    };
    return stepFieldMap[stepNumber] || [];
  };
  
  const getFieldEventName = (fieldName, action) => {
    if (action !== 'change') return null;
    
    const eventMap = {
      userName: 'user_name_entered',
      jobTitle: 'job_title_entered',
      companyName: 'company_name_entered',
      companySize: 'company_size_selected',
      mainGoals: 'main_goals_selected',
      expectedVolume: 'expected_volume_selected'
    };
    
    return eventMap[fieldName] || null;
  };
  
  const getBaseEventName = (fieldName) => {
    const eventMap = {
      hearAboutUs: 'referral_source_selected',
      industry: 'company_industry_selected',
      primaryUseCase: 'primary_use_case_selected'
    };
    return eventMap[fieldName] || null;
  };
  
  
  const getUseCaseComplexity = (useCase) => {
    const complexity = {
      'Customer Support': 'simple',
      'Sales Automation': 'intermediate',
      'Lead Generation': 'intermediate',
      'Appointment Scheduling': 'simple',
      'Technical Support': 'advanced',
      'Custom Integration': 'advanced',
      'Other': 'unknown'
    };
    return complexity[useCase] || 'unknown';
  };
  
  // Navigation functions
  const goToNextStep = useCallback(() => {
    if (currentStep < 4) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      trackStepStart(nextStep, getStepName(nextStep));
    }
  }, [currentStep, trackStepStart]);
  
  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      trackStepStart(prevStep, getStepName(prevStep));
    }
  }, [currentStep, trackStepStart]);
  
  const getStepName = (stepNumber) => {
    const names = {
      1: 'personal_info',
      2: 'company_info',
      3: 'ai_requirements',
      4: 'completion'
    };
    return names[stepNumber] || `step_${stepNumber}`;
  };
  
  // Data update function
  const updateOnboardingData = useCallback((data) => {
    setOnboardingData(prev => ({ ...prev, ...data }));
  }, []);
  
  const value = {
    // State
    currentStep,
    onboardingData,
    isCompleted,
    
    // Navigation
    goToNextStep,
    goToPreviousStep,
    setCurrentStep,
    
    // Data management
    updateOnboardingData,
    
    // Analytics tracking
    initializeOnboarding,
    trackStepStart,
    trackStepCompleted,
    trackStepSkipped,
    trackValidationFailure,
    trackFieldInteraction,
    trackOnboardingCompleted,
    trackOnboardingAbandoned,
    trackSpecialFieldSelection
  };
  
  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};